import React, { memo, useMemo } from "react";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import Text from "@ui/Text";
import { getRoleIcon, getRoleColor, resolveRole, type Role } from "./roleConfig";
import t from "../../../../../../../../i18n";
import { uniq } from "diagnostics-lib/array";
import { useTranslations } from "../../../../../../../../api/useTranslations";
import type { Annotation } from "./Annotation/hook/useAnnotations";
import { useAvailableResources } from "../../../../../hook/useAvailableResources";
import { CircleFill, CircleLine, TriangleFill } from "@ui/IconV2/react";

const ROLE_ORDER: Role[] = [
  "QUANTITY",
  "PERFORMANCE_INDICATOR",
  "MODEL_INPUT",
  "OBSERVABLE",
  "INACTIVE_OBSERVABLE",
  "OTHER_QUANTITY",
  "FAULT",
  "SYSTEM",
  "UNEXPLAINED_LOSSES",
];

export const DiagramLegend = memo(
  ({
    projectId,
    annotations,
    isRoleDisplayed,
  }: {
    projectId: string;
    annotations: Annotation[];
    isRoleDisplayed: boolean;
  }) => {
    const availableResources = useAvailableResources({ projectId });
    const translations = useTranslations();

    const visibleRoles = useMemo((): Role[] => {
      const resolvedRoles = annotations.map(a =>
        resolveRole(a, availableResources, isRoleDisplayed),
      );
      const normalizedRoles = isRoleDisplayed
        ? resolvedRoles
        : resolvedRoles.map(role =>
            ["MODEL_INPUT", "OBSERVABLE", "INACTIVE_OBSERVABLE", "PERFORMANCE_INDICATOR"].includes(
              role,
            )
              ? "QUANTITY"
              : role,
          );
      return ROLE_ORDER.filter(role => uniq(normalizedRoles).includes(role));
    }, [annotations, availableResources, isRoleDisplayed]);

    const visibleValueTypes = useMemo(() => {
      const columns = new Set(annotations.flatMap(a => a.values.map(v => v.column)));
      const types: { key: string; icon: React.ReactNode; label: string }[] = [];

      if (columns.has("measured")) {
        types.push({
          key: "measured",
          icon: <CircleFill size="m" color={colors.titanium600} />,
          label: t("project.dashboard.resource.column.annotation", "measured"),
        });
      }
      if (columns.has("expected")) {
        types.push({
          key: "expected",
          icon: <CircleLine size="m" color={colors.titanium600} />,
          label: t("project.dashboard.resource.column.annotation", "expected"),
        });
      }
      if (columns.has("std")) {
        types.push({
          key: "std",
          icon: <span className={stdLabel}>STD</span>,
          label: t("project.dashboard.resource.column.annotation", "std"),
        });
      }
      if (columns.has("gap")) {
        types.push({
          key: "gap",
          icon: <TriangleFill size="m" color={colors.titanium600} />,
          label: t("project.dashboard.resource.column.annotation", "gap"),
        });
      }
      if (columns.has("normalizedGap") && !columns.has("gap")) {
        types.push({
          key: "normalizedGap",
          icon: <TriangleFill size="m" color={colors.titanium600} />,
          label: t("project.dashboard.resource.column.annotation", "normalizedGap"),
        });
      }

      return types;
    }, [annotations]);

    return (
      <div className={legendContainer}>
        {visibleRoles.map(role => {
          const IconComponent = getRoleIcon(role);
          const color = getRoleColor(role);
          const iconFill =
            role === "INACTIVE_OBSERVABLE" ? fillIconInactiveObservable : fillWithCurrentColor;

          return (
            <div key={role} className={legendItem}>
              <div className={legendIcon}>
                <IconComponent size="m" color={color} className={iconFill} />
              </div>
              <Text type="body3SemiBold" color={colors.carbon800}>
                {t("project.dashboard.role.label", role, translations)}
              </Text>
            </div>
          );
        })}
        {visibleValueTypes.length > 0 && (
          <>
            <div className={legendSeparator} />
            {visibleValueTypes.map(({ key, icon, label }) => (
              <div key={key} className={legendItem}>
                <div className={legendIcon}>{icon}</div>
                <Text type="body3SemiBold" color={colors.carbon800}>
                  {label}
                </Text>
              </div>
            ))}
          </>
        )}
      </div>
    );
  },
);

const legendIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: ${colors.chart_categorical_blue};
`;

const legendContainer = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacings.m};
  margin-top: ${spacings.m};
`;

const legendItem = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
`;

const legendSeparator = css`
  width: 1px;
  height: 16px;
  background-color: ${colors.titanium300};
  margin: 0 ${spacings.xs};
`;

const stdLabel = css`
  color: ${colors.titanium600};
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
`;

const fillWithCurrentColor = css`
  path {
    fill: currentColor;
  }
`;

const fillIconInactiveObservable = css`
  path:not(:last-of-type),
  rect {
    fill: ${colors.titanium500};
  }

  path:last-of-type {
    fill: ${colors.hydrogen};
  }
`;
